import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import {
    getSubscriptions,
    subscribeToEntity,
    subscribeToEvent,
    unsubscribe,
    getPreferences,
    updatePreferences,
    getPlatformChannels,
    updatePlatformChannelAlerts,
    ApiError
} from '../api/notifications';
import {
    checkBrowserSupport,
    getNotificationPermission,
    requestNotificationPermission,
    ensurePushRegistration,
    unregisterPushSubscriptionForDevice
} from '../services/notificationService';

// Error handling utility based on API documentation
const handleApiError = (error) => {
    // Map error codes to user-friendly messages
    const userMessages = {
        'MISSING_REQUIRED_FIELD': error.message || 'Some required information is missing. Please check your input.',
        'INVALID_INPUT': error.message || 'The information provided is invalid. Please check and try again.',
        'VALIDATION_ERROR': error.message || 'Validation error. Please check your input.',
        'UNAUTHORIZED': 'Please log in to subscribe to notifications.',
        'EVENT_NOT_FOUND': 'This event no longer exists.',
        'ENTITY_NOT_FOUND': 'This entity no longer exists.',
        'USER_NOT_FOUND': 'User profile not found. Please log in again.',
        'RECORD_NOT_FOUND': 'The requested record was not found.',
        'UNIQUE_CONSTRAINT_VIOLATION': 'You are already subscribed to this event.',
        'DATABASE_ERROR': 'We encountered an issue. Please try again.',
        'REQUIRED_FIELD_MISSING': error.message || 'Some required information is missing. Please try again.',
        'FOREIGN_KEY_CONSTRAINT_VIOLATION': 'The referenced record does not exist.',
        'INTERNAL_SERVER_ERROR': 'Something went wrong. Please try again later.'
    };

    const message = userMessages[error.code] || error.message || 'An error occurred. Please try again.';
    
    // Log technical details for debugging
    console.error('Subscription error:', {
        code: error.code,
        details: error.details,
        field: error.field,
        timestamp: error.timestamp
    });
    
    return {
        message,
        code: error.code,
        field: error.field,
        details: error.details,
        isRetryable: ['DATABASE_ERROR', 'INTERNAL_SERVER_ERROR'].includes(error.code)
    };
};

const NotificationContext = createContext();

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const { isLoggedIn } = useAuth();
    const [subscriptions, setSubscriptions] = useState([]);
    const [preferences, setPreferences] = useState(null);
    const [preferencesError, setPreferencesError] = useState(null);
    const [platformChannels, setPlatformChannels] = useState([]);
    const [platformChannelsError, setPlatformChannelsError] = useState(null);
    const [pushSubscription, setPushSubscription] = useState(null);
    const [permissionStatus, setPermissionStatus] = useState('default');
    const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
    const [platformChannelsLoading, setPlatformChannelsLoading] = useState(false);
    const [preferencesSaving, setPreferencesSaving] = useState(false);
    const [error, setError] = useState(null);

    // Check browser support and permission status
    useEffect(() => {
        if (typeof window !== 'undefined') {
            if (checkBrowserSupport()) {
                setPermissionStatus(getNotificationPermission());
            } else {
                setPermissionStatus('unsupported');
            }
        }
    }, []);

    // Initialize push subscription when user logs in
    useEffect(() => {
        if (isLoggedIn && getNotificationPermission() === 'granted') {
            initializePushSubscription();
        }
    }, [isLoggedIn]);

    // Load subscriptions, preferences and platform channels when user logs in
    useEffect(() => {
        if (isLoggedIn) {
            refreshSubscriptions();
            refreshPreferences();
            refreshPlatformChannels();
        } else {
            setSubscriptions([]);
            setPreferences(null);
            setPreferencesError(null);
            setPlatformChannels([]);
            setPlatformChannelsError(null);
        }
    }, [isLoggedIn]);

    const initializePushSubscription = async () => {
        try {
            // Always ensure backend has an endpoint when permission is already granted
            // (heals empty DB / lost PushSubscription cases).
            const subscription = await ensurePushRegistration();
            setPushSubscription(subscription);
        } catch (error) {
            console.error('Error initializing push subscription:', error);
        }
    };

    const refreshSubscriptions = useCallback(async () => {
        if (!isLoggedIn) return;
        
        try {
            setSubscriptionsLoading(true);
            setError(null);
            const data = await getSubscriptions();
            setSubscriptions(data);
        } catch (err) {
            console.error('Error refreshing subscriptions:', err);
            setError(err.message || 'Failed to load subscriptions');
        } finally {
            setSubscriptionsLoading(false);
        }
    }, [isLoggedIn]);

    const refreshPreferences = useCallback(async () => {
        if (!isLoggedIn) return;
        
        try {
            setPreferencesError(null);
            const data = await getPreferences();
            setPreferences(data);
        } catch (err) {
            console.error('Error refreshing preferences:', err);
            // Do not fall back to defaults: stale defaults could overwrite
            // server state on save. Consumers must block save until loaded.
            setPreferences(null);
            setPreferencesError(err.message || 'Failed to load notification preferences');
        }
    }, [isLoggedIn]);

    const refreshPlatformChannels = useCallback(async () => {
        if (!isLoggedIn) return;

        try {
            setPlatformChannelsLoading(true);
            setPlatformChannelsError(null);
            const data = await getPlatformChannels();
            setPlatformChannels(data);
        } catch (err) {
            console.error('Error refreshing platform channels:', err);
            setPlatformChannelsError(err.message || 'Failed to load platform channels');
        } finally {
            setPlatformChannelsLoading(false);
        }
    }, [isLoggedIn]);

    const setChannelAlertsEnabled = useCallback(async (channelId, alertsEnabled) => {
        if (!isLoggedIn) {
            throw new Error('You must be logged in to update channel alerts');
        }

        if (alertsEnabled) {
            let permission = getNotificationPermission();
            if (permission !== 'granted') {
                permission = await requestNotificationPermission();
                setPermissionStatus(permission);
            }
            if (permission !== 'granted') {
                throw new Error(
                    'Browser notification permission is required for live alerts. Enable notifications and try again.'
                );
            }
            try {
                const subscription = await ensurePushRegistration();
                setPushSubscription(subscription);
            } catch (err) {
                console.error('Error registering push subscription for channel alerts:', err);
                throw new Error(
                    err.message || 'Failed to register this device for push notifications. Live alerts will not work until registration succeeds.'
                );
            }
        }

        setPlatformChannels(prev => prev.map(ch => (
            ch.id === channelId ? { ...ch, alertsEnabled } : ch
        )));

        try {
            await updatePlatformChannelAlerts(channelId, alertsEnabled);
        } catch (err) {
            setPlatformChannels(prev => prev.map(ch => (
                ch.id === channelId ? { ...ch, alertsEnabled: !alertsEnabled } : ch
            )));
            throw err;
        }
    }, [isLoggedIn]);

    const requestPermission = useCallback(async () => {
        const permission = await requestNotificationPermission();
        setPermissionStatus(permission);

        if (permission === 'granted') {
            // Propagate registration failures to the UI — permission alone is not enough.
            const subscription = await ensurePushRegistration();
            setPushSubscription(subscription);
        }

        return permission;
    }, []);

    const subscribeToEntityNotification = useCallback(async (entityId, channels = ['web'], isMainEvent = false) => {
        if (!isLoggedIn) {
            throw new Error('You must be logged in to subscribe');
        }

        try {
            setError(null);

            // Request permission if needed
            if (channels.includes('web') && permissionStatus !== 'granted') {
                const permission = await requestPermission();
                if (permission !== 'granted') {
                    throw new Error('Notification permission is required for web push notifications');
                }
            }

            const subscription = await subscribeToEntity(entityId, channels, isMainEvent);
            await refreshSubscriptions();
            return subscription;
        } catch (err) {
            // Handle structured API errors
            if (err instanceof ApiError) {
                const errorInfo = handleApiError(err);
                setError(errorInfo.message);
                // Create enhanced error with field information
                const enhancedError = new Error(errorInfo.message);
                enhancedError.code = errorInfo.code;
                enhancedError.field = errorInfo.field;
                enhancedError.isRetryable = errorInfo.isRetryable;
                throw enhancedError;
            }
            // Handle other errors
            const errorMessage = err.message || 'Failed to subscribe to entity';
            setError(errorMessage);
            throw err;
        }
    }, [isLoggedIn, permissionStatus, requestPermission, refreshSubscriptions]);

    const subscribeToEventNotification = useCallback(async (eventId, channels = ['web'], isMainEvent = false) => {
        if (!isLoggedIn) {
            throw new Error('You must be logged in to subscribe');
        }

        try {
            setError(null);

            // Request permission if needed
            if (channels.includes('web') && permissionStatus !== 'granted') {
                const permission = await requestPermission();
                if (permission !== 'granted') {
                    throw new Error('Notification permission is required for web push notifications');
                }
            }

            const subscription = await subscribeToEvent(eventId, channels, isMainEvent);
            await refreshSubscriptions();
            return subscription;
        } catch (err) {
            // Handle structured API errors
            if (err instanceof ApiError) {
                const errorInfo = handleApiError(err);
                setError(errorInfo.message);
                // Create enhanced error with field information
                const enhancedError = new Error(errorInfo.message);
                enhancedError.code = errorInfo.code;
                enhancedError.field = errorInfo.field;
                enhancedError.isRetryable = errorInfo.isRetryable;
                throw enhancedError;
            }
            // Handle other errors
            const errorMessage = err.message || 'Failed to subscribe to event';
            setError(errorMessage);
            throw err;
        }
    }, [isLoggedIn, permissionStatus, requestPermission, refreshSubscriptions]);

    const unsubscribeFromNotification = useCallback(async (subscriptionId) => {
        if (!isLoggedIn) {
            throw new Error('You must be logged in to unsubscribe');
        }

        try {
            setError(null);
            await unsubscribe(subscriptionId);
            await refreshSubscriptions();
        } catch (err) {
            // Handle structured API errors
            if (err instanceof ApiError) {
                const errorInfo = handleApiError(err);
                setError(errorInfo.message);
                throw new Error(errorInfo.message);
            }
            // Handle other errors
            const errorMessage = err.message || 'Failed to unsubscribe';
            setError(errorMessage);
            throw err;
        }
    }, [isLoggedIn, refreshSubscriptions]);

    const updateNotificationPreferences = useCallback(async (newPreferences) => {
        if (!isLoggedIn) {
            throw new Error('You must be logged in to update preferences');
        }

        try {
            setPreferencesSaving(true);
            setError(null);

            // Register this device before saving opt-in so delivery can succeed.
            // iOS Safari in a tab has no Notification global — still save quiet hours / timezone.
            if (newPreferences.webPushOptIn === true && checkBrowserSupport()) {
                let permission = getNotificationPermission();
                if (permission !== 'granted') {
                    permission = await requestNotificationPermission();
                    setPermissionStatus(permission);
                }
                if (permission !== 'granted') {
                    throw new Error(
                        'Browser notification permission is required for web push notifications.'
                    );
                }
                const subscription = await ensurePushRegistration();
                setPushSubscription(subscription);
            }

            const updated = await updatePreferences(newPreferences);
            setPreferences(updated);

            // Multi-device push: unregister this device when web push is turned off
            if (newPreferences.webPushOptIn === false) {
                try {
                    const unregistered = await unregisterPushSubscriptionForDevice();
                    if (unregistered) {
                        setPushSubscription(null);
                    }
                } catch (err) {
                    console.error('Error unregistering push subscription for device:', err);
                }
            }

            return updated;
        } catch (err) {
            const errorMessage = err.message || 'Failed to update preferences';
            setError(errorMessage);
            throw err;
        } finally {
            setPreferencesSaving(false);
        }
    }, [isLoggedIn]);

    // Helper to check if event is subscribed
    const isEventSubscribed = useCallback((eventId) => {
        return subscriptions.some(sub => 
            sub.eventId === eventId && sub.notificationType === 'specific_event'
        );
    }, [subscriptions]);

    // Helper to check if entity is subscribed
    const isEntitySubscribed = useCallback((entityId) => {
        return subscriptions.some(sub => 
            sub.entityId === entityId && sub.notificationType === 'entity_live'
        );
    }, [subscriptions]);

    // Helper to get subscription for event
    const getEventSubscription = useCallback((eventId) => {
        return subscriptions.find(sub => 
            sub.eventId === eventId && sub.notificationType === 'specific_event'
        );
    }, [subscriptions]);

    // Helper to get subscription for entity
    const getEntitySubscription = useCallback((entityId) => {
        return subscriptions.find(sub => 
            sub.entityId === entityId && sub.notificationType === 'entity_live'
        );
    }, [subscriptions]);

    const value = {
        subscriptions,
        preferences,
        preferencesError,
        platformChannels,
        platformChannelsError,
        pushSubscription,
        permissionStatus,
        subscriptionsLoading,
        platformChannelsLoading,
        preferencesSaving,
        error,
        refreshSubscriptions,
        refreshPreferences,
        refreshPlatformChannels,
        setChannelAlertsEnabled,
        requestPermission,
        subscribeToEntity: subscribeToEntityNotification,
        subscribeToEvent: subscribeToEventNotification,
        unsubscribe: unsubscribeFromNotification,
        updatePreferences: updateNotificationPreferences,
        isEventSubscribed,
        isEntitySubscribed,
        getEventSubscription,
        getEntitySubscription,
        checkBrowserSupport: checkBrowserSupport()
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};






